import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { EASE } from "@/lib/motion";
import { scrollToSection } from "@/lib/scroll";

const HERO_IMG =
    "https://images.pexels.com/photos/14541770/pexels-photo-14541770.jpeg?auto=compress&cs=tinysrgb&w=1600";

function Line({ children, delay }) {
    return (
        <div className="overflow-hidden">
            <motion.div
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.1, delay, ease: EASE }}
                className="will-change-transform"
            >
                {children}
            </motion.div>
        </div>
    );
}

export default function Hero() {
    const imgWrap = useRef(null);
    const { scrollYProgress } = useScroll({ target: imgWrap, offset: ["start end", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

    return (
        <section id="top" className="relative px-5 md:px-10 pt-24 md:pt-28 pb-10 md:pb-14 flex flex-col min-h-screen">
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.9, duration: 0.8 }}
                className="flex justify-between gap-4 text-[10px] md:text-[11px] font-semibold uppercase tracking-[0.22em] text-inkdim border-b border-line pb-4"
            >
                <span data-testid="hero-name">Hitanshu Bhadresh Shah</span>
                <span className="text-right">Mumbai — India</span>
            </motion.div>

            <div className="grid grid-cols-12 gap-8 flex-1 items-end pt-10 md:pt-14">
                <div className="col-span-12 xl:col-span-8">
                    <h1 className="font-display font-extrabold uppercase tracking-[-0.02em] leading-[0.96] text-[clamp(2.4rem,6.9vw,6.4rem)]">
                        <Line delay={0.1}>I create images</Line>
                        <Line delay={0.22}>
                            <span className="flex flex-wrap items-baseline gap-x-[0.22em]">
                                <span>that help</span>
                                <em className="font-serif italic font-medium normal-case tracking-normal text-[1.06em] leading-none">
                                    stories
                                </em>
                            </span>
                        </Line>
                        <Line delay={0.34}>
                            <span className="flex flex-wrap items-baseline gap-x-[0.22em]">
                                <span>&amp; brands</span>
                                <em className="font-serif italic font-medium normal-case tracking-normal text-[1.06em] leading-none">
                                    communicate
                                </em>
                            </span>
                        </Line>
                    </h1>

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.75, duration: 0.9, ease: EASE }}
                        className="mt-8 md:mt-12 max-w-2xl"
                    >
                        <p
                            className="text-[10px] md:text-xs font-bold uppercase tracking-[0.22em] text-ink"
                            data-testid="hero-roles"
                        >
                            Writer&nbsp;&nbsp;/&nbsp;&nbsp;Cinematographer&nbsp;&nbsp;/&nbsp;&nbsp;Editor
                        </p>
                        <p className="mt-4 text-base md:text-lg leading-relaxed text-inkdim">
                            Independent Mumbai-based filmmaker building contemporary visual stories for brands,
                            productions and independent projects — from first idea to final frame.
                        </p>
                        <div className="mt-8 flex flex-wrap items-center gap-4 md:gap-6">
                            <button
                                data-testid="hero-cta-work"
                                onClick={() => scrollToSection("#work")}
                                className="group inline-flex items-center gap-3 bg-ink text-paper text-xs font-bold uppercase tracking-[0.2em] px-7 py-4 hover:bg-inkdim transition-colors duration-300 cursor-pointer"
                            >
                                See the work
                                <ArrowDown size={14} className="transition-transform duration-300 group-hover:translate-y-0.5" />
                            </button>
                            <button
                                data-testid="hero-cta-contact"
                                onClick={() => scrollToSection("#contact")}
                                className="text-xs font-bold uppercase tracking-[0.2em] border-b border-ink pb-1 hover:opacity-60 transition-opacity duration-300 cursor-pointer"
                            >
                                Start a project
                            </button>
                        </div>
                    </motion.div>
                </div>

                <motion.div
                    ref={imgWrap}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.55, duration: 1 }}
                    className="hidden xl:block col-span-4 relative"
                >
                    <div className="relative overflow-hidden aspect-[3/4]">
                        <motion.img
                            data-testid="hero-image"
                            src={HERO_IMG}
                            alt="Cinematic night frame — Mumbai"
                            style={{ y, scale: 1.16 }}
                            className="absolute inset-0 h-full w-full object-cover"
                        />
                    </div>
                    <div className="flex justify-between pt-3 text-[10px] uppercase tracking-[0.2em] text-inkdim">
                        <span>Frame 01 — Mumbai</span>
                        <span>Available for work</span>
                    </div>
                </motion.div>
            </div>

            <motion.div
                initial={{ opacity: 0, clipPath: "inset(0 0 100% 0)" }}
                animate={{ opacity: 1, clipPath: "inset(0 0 0% 0)" }}
                transition={{ delay: 0.9, duration: 1, ease: EASE }}
                className="xl:hidden mt-12 overflow-hidden aspect-[16/10]"
            >
                <img src={HERO_IMG} alt="Cinematic night frame — Mumbai" className="h-full w-full object-cover" />
            </motion.div>
        </section>
    );
}
